import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { AdvancedFilterModel, MagicSearchModel, stemsEQModel } from 'src/app/models/magic-search.model';
import { DEFAULT_STEMS } from 'src/app/constants/magic-search.constants';
import { AudioAttachment } from 'src/app/components/shared/magic-search/magic-search-audio-modal/magic-search-audio-modal.component';

@Injectable({ providedIn: 'root' })
export class MagicSearchService {
  pendingPayload$ = new BehaviorSubject<MagicSearchModel | null>(null);
  // AI (magic) search is the default; CL only when chosen in this tab
  searchMode$ = new BehaviorSubject<'classic' | 'magic'>(
    sessionStorage.getItem('searchMode') === 'classic' ? 'classic' : 'magic'
  );

  stemsEq$ = new BehaviorSubject<{ stems: stemsEQModel; eqEnabled: boolean }>({
    stems: { ...DEFAULT_STEMS, levels: { ...DEFAULT_STEMS.levels } },
    eqEnabled: true,
  });
  
  filtersAppliedFromMobile$ = new Subject<void>();

  filtersUpdateFromFilterRow$ = new Subject<AdvancedFilterModel>();

  attachmentsState$ = new BehaviorSubject<{ hasLyrics: boolean; audioName: string | null }>({
    hasLyrics: false,
    audioName: null,
  });
  removeLyricsRequest$ = new Subject<void>();
  removeAudioRequest$ = new Subject<void>();
  clearQueryTextRequest$ = new Subject<void>();

  /**
   * Mirrors the current lyrics text so the FilterSectionComponent's attachment
   * chip can show/edit it without owning the text itself — the live copy still
   * lives on the MagicSearchComponent instance that owns the search bar.
   */
  lyricsText$ = new BehaviorSubject<string>('');
  /** FilterSectionComponent's chip has no dialog of its own — asks the live
   *  MagicSearchComponent instance to open its lyrics popup (add or edit). */
  openLyricsDialogRequest$ = new Subject<void>();

  setLyricsText(text: string): void {
    this.lyricsText$.next(text);
  }

  requestOpenLyricsDialog(): void {
    this.openLyricsDialogRequest$.next();
  }

  /**
   * Bridges play/pause between the attachment chip shown by FilterSectionComponent
   * (no <audio> element of its own) and the MagicSearchComponent instance that
   * actually owns the audio playback — mirrors the removeAudioRequest$ pattern.
   */
  togglePlaybackRequest$ = new Subject<void>();
  audioPlayingState$ = new BehaviorSubject<boolean>(false);

  requestTogglePlayback(): void {
    this.togglePlaybackRequest$.next();
  }

  setAudioPlayingState(isPlaying: boolean): void {
    this.audioPlayingState$.next(isPlaying);
  }

  /**
   * The live attachment (Blob included) for whatever audio is currently attached
   * to a magic search. Root-provided, so it survives the landing-page → /search
   * navigation and every filter-driven re-hydrate on /search — letting a rehydrated
   * MagicSearchComponent instance recover the real Blob (and correct MIME type) for
   * playback instead of only the base64 bytes carried in the search payload.
   */
  attachedAudio$ = new BehaviorSubject<AudioAttachment | null>(null);

  get attachedAudio(): AudioAttachment | null {
    return this.attachedAudio$.getValue();
  }

  setAttachedAudio(attachment: AudioAttachment | null): void {
    this.attachedAudio$.next(attachment);
  }

  setPending(payload: MagicSearchModel): void {
    this.pendingPayload$.next(payload);
  }

  clearPending(): void {
    this.pendingPayload$.next(null);
  }

  get searchMode(): 'classic' | 'magic' {
    return this.searchMode$.getValue();
  }

  setSearchMode(mode: 'classic' | 'magic'): void {
    sessionStorage.setItem('searchMode', mode);
    if (this.searchMode$.getValue() !== mode) {
      this.searchMode$.next(mode);
    }
  }

  setStemsEq(stems: stemsEQModel, eqEnabled: boolean): void {
    this.stemsEq$.next({ stems, eqEnabled });
  }

  triggerFiltersAppliedFromMobile(): void {
    this.filtersAppliedFromMobile$.next();
  }

  pushFiltersUpdate(filters: AdvancedFilterModel): void {
    this.filtersUpdateFromFilterRow$.next(filters);
  }

  setAttachmentsState(hasLyrics: boolean, audioName: string | null): void {
    this.attachmentsState$.next({ hasLyrics, audioName });
  }

  requestRemoveLyrics(): void {
    this.removeLyricsRequest$.next();
  }

  requestRemoveAudio(): void {
    this.removeAudioRequest$.next();
  }

  requestClearQueryText(): void {
    this.clearQueryTextRequest$.next();
  }
}
